import React, { useState, useRef, useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import FlashcardGame from './components/FlashcardGame';
import HomePage from './components/HomePage';
import SynonymGame from './components/SynonymGame';
import FastAttackGame from './components/FastAttackGame';
import HardTermsGame from './components/HardTermsGame';
import QuantProblemTracker from './components/QuantProblemTracker';
import GeometryLearning from './components/GeometryLearning';
import TimeStats from './components/TimeStats';
import AuthModal from './components/AuthModal';
import FeedbackModal from './components/FeedbackModal';
import { AppHeader } from './components/phrenos-brand';
import { TrialBanner, PastDueBanner, CouponAppliedBanner } from './components/SubscriptionBanners';
import InstallBanner from './components/InstallBanner';
import LandingPage from './pages/LandingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import UpgradePage from './pages/UpgradePage';
import { useAuth } from './context/AuthContext';
import { useSubscription } from './hooks/useSubscription';
import { supabase } from './services/supabaseClient';
import './App.css';

const PRIMARY   = 'var(--color-primary)';
const BORDER    = 'var(--color-border)';
const TEXT_MUTED = 'var(--color-text-muted)';

// SVG icons for tab bar (20px)
const TabIcons: Record<string, React.ReactNode> = {
  home: (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M3 12L12 3l9 9" /><path d="M9 21V12h6v9" />
    </svg>
  ),
  flashcard: (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M6 6h12v12H6z" /><path d="M10 10h6M10 14h4" />
    </svg>
  ),
  synonym: (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <circle cx="8" cy="12" r="3" /><circle cx="16" cy="12" r="3" /><path d="M11 12h2" />
    </svg>
  ),
  fastattack: (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M13 2L6 14h5l-1 8 7-12h-5z" />
    </svg>
  ),
  hardterms: (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M12 2s5 5 5 9a5 5 0 0 1-10 0c0-4 5-9 5-9z" /><path d="M12 22v-4" />
    </svg>
  ),
  quantproblem: (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M4 20h16" /><path d="M10 20V4h4v16" /><path d="M4 10h16" />
    </svg>
  ),
};

const TAB_ROUTES: { path: string; id: string; label: string }[] = [
  { path: '/',            id: 'home',         label: 'Home'       },
  { path: '/flashcards',  id: 'flashcard',    label: 'Flashcards' },
  { path: '/fast-attack', id: 'fastattack',   label: 'Fast Attack'},
  { path: '/hard-terms',  id: 'hardterms',    label: 'Hard Terms' },
  { path: '/quant',       id: 'quantproblem', label: 'Quant'      },
];

// Post-trial downgrade modal — shown once per session after trial expires
const POST_TRIAL_DISMISSED_KEY = 'phrenos_post_trial_modal_dismissed';

function PostTrialModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);
  return (
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
    >
      <div style={{
        backgroundColor: 'var(--color-surface)', borderRadius: 20,
        padding: '28px 24px', maxWidth: 340, width: '100%', textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%',
          backgroundColor: 'var(--color-success-light)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px', fontSize: 24,
        }}>⏰</div>
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 8px', color: 'var(--color-text)' }}>
          Your trial has ended
        </h2>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.5, margin: '0 0 24px' }}>
          Subscribe to keep access to all 1,100+ words, unlimited sessions, and full sync.
        </p>
        <button
          onClick={() => { navigate('/upgrade'); onClose(); }}
          style={{
            width: '100%', padding: '13px 0',
            backgroundColor: 'var(--color-button)', color: '#fff',
            border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700,
            cursor: 'pointer', marginBottom: 10,
          }}
        >
          See Pro plans →
        </button>
        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '10px 0', background: 'transparent',
            color: 'var(--color-text-muted)', border: 'none', borderRadius: 12,
            fontSize: 14, cursor: 'pointer',
          }}
        >
          Continue with free plan
        </button>
      </div>
    </div>
  );
}

function App() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showPostTrialModal, setShowPostTrialModal] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const { user, loading: authLoading, signOut } = useAuth();
  const { isPro, isTrial, plan, trialEndsAt } = useSubscription();
  const profileRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Show post-trial modal once per session when trial has expired
  useEffect(() => {
    if (!user || isTrial || plan !== 'free') return;
    const trialExpired = trialEndsAt != null && trialEndsAt < new Date();
    if (!trialExpired) return;
    if (sessionStorage.getItem(POST_TRIAL_DISMISSED_KEY) === '1') return;
    setShowPostTrialModal(true);
  }, [user, isTrial, plan, trialEndsAt]);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    setShowProfileMenu(false);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-portal`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ returnUrl: window.location.href }),
        }
      );
      const { url, error } = await res.json();
      if (error) throw new Error(error);
      window.location.href = url;
    } catch (err) {
      console.error('[App] portal error:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  const isActive = (path: string) =>
    path === '/' ? pathname === '/' : pathname.startsWith(path);

  // Close profile menu when clicking outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfileMenu(false);
      }
    }
    if (showProfileMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showProfileMenu]);

  const handleSignOut = async () => {
    setSigningOut(true);
    setShowProfileMenu(false);
    await signOut();
    setSigningOut(false);
  };

  // ── Auth gate: show nothing while checking session, landing page if logged out ──
  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#E6F1EC' }}>
        <Analytics />
      </div>
    );
  }

  if (!user) {
    if (pathname === '/privacy') return <PrivacyPolicy />;
    if (pathname === '/terms') return <TermsOfService />;
    return (
      <>
        <LandingPage onSignIn={() => setShowAuthModal(true)} />
        <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
        <Analytics />
      </>
    );
  }

  return (
    <div className="App" style={{ minHeight: '100vh', backgroundColor: 'var(--color-background)' }}>

      {/* ── Phrenos header (48px) ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100 }}>
        <AppHeader onProfilePress={() => setShowProfileMenu(p => !p)} planBadge={isTrial ? 'TRIAL' : isPro ? 'PRO' : undefined} />

        {/* Profile dropdown */}
        {showProfileMenu && (
          <div ref={profileRef}
            style={{
              position: 'absolute',
              top: 52,
              right: 16,
              backgroundColor: 'var(--color-surface)',
              border: `1px solid ${BORDER}`,
              borderRadius: 14,
              padding: '12px',
              minWidth: 200,
              zIndex: 200,
            }}
          >
            {user ? (
              <>
                <p style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 10, padding: '0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.email}
                </p>
                <button
                  onClick={handleSignOut}
                  disabled={signingOut}
                  style={{
                    width: '100%', padding: '9px 14px', fontSize: 14, fontWeight: 500,
                    border: `1.5px solid ${BORDER}`, borderRadius: 10,
                    backgroundColor: 'transparent', color: 'var(--color-text)',
                    cursor: signingOut ? 'not-allowed' : 'pointer',
                    textAlign: 'left', marginBottom: 8,
                  }}
                >
                  {signingOut ? 'Signing out…' : 'Sign Out'}
                </button>
                <button
                  onClick={() => {
                    if (isPro) { navigate('/time'); setShowProfileMenu(false); }
                    else { navigate('/upgrade'); setShowProfileMenu(false); }
                  }}
                  style={{
                    width: '100%', padding: '9px 14px', fontSize: 14, fontWeight: 500,
                    border: 'none', borderRadius: 10,
                    backgroundColor: 'var(--color-surface-muted)', color: TEXT_MUTED,
                    cursor: 'pointer', textAlign: 'left', marginBottom: 6,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                >
                  <span>Time & Stats</span>
                  {!isPro && <span style={{ fontSize: 11 }}>🔒</span>}
                </button>
                <button
                  onClick={() => { navigate('/geometry'); setShowProfileMenu(false); }}
                  style={{
                    width: '100%', padding: '9px 14px', fontSize: 14, fontWeight: 500,
                    border: 'none', borderRadius: 10,
                    backgroundColor: 'var(--color-surface-muted)', color: TEXT_MUTED,
                    cursor: 'pointer', textAlign: 'left', marginBottom: 6,
                  }}
                >
                  Geometry Learning
                </button>
                <button
                  onClick={() => { setShowFeedbackModal(true); setShowProfileMenu(false); }}
                  style={{
                    width: '100%', padding: '9px 14px', fontSize: 14, fontWeight: 500,
                    border: 'none', borderRadius: 10,
                    backgroundColor: 'var(--color-surface-muted)', color: TEXT_MUTED,
                    cursor: 'pointer', textAlign: 'left', marginBottom: 6,
                  }}
                >
                  Send Feedback
                </button>
                {plan === 'pro' && (
                  <button
                    onClick={handleOpenPortal}
                    disabled={portalLoading}
                    style={{
                      width: '100%', padding: '9px 14px', fontSize: 14, fontWeight: 500,
                      border: 'none', borderRadius: 10,
                      backgroundColor: 'var(--color-surface-muted)', color: TEXT_MUTED,
                      cursor: portalLoading ? 'not-allowed' : 'pointer', textAlign: 'left',
                    }}
                  >
                    {portalLoading ? 'Loading…' : 'Manage Subscription'}
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={() => { setShowAuthModal(true); setShowProfileMenu(false); }}
                disabled={authLoading}
                style={{
                  width: '100%', padding: '10px 14px', fontSize: 14, fontWeight: 600,
                  border: 'none', borderRadius: 10,
                  backgroundColor: PRIMARY, color: '#fff',
                  cursor: authLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {authLoading ? 'Loading…' : 'Sign In'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Subscription banners (trial countdown / past-due alert) ── */}
      <CouponAppliedBanner />
      <TrialBanner />
      <PastDueBanner />
      {user && <InstallBanner />}

      {/* ── Post-trial downgrade modal ── */}
      {showPostTrialModal && (
        <PostTrialModal onClose={() => {
          sessionStorage.setItem(POST_TRIAL_DISMISSED_KEY, '1');
          setShowPostTrialModal(false);
        }} />
      )}

      {/* ── Main content ── */}
      <div
        style={{
          padding: '16px 16px',
          paddingBottom: 'calc(76px + env(safe-area-inset-bottom, 0px))',
          maxWidth: 1100,
          margin: '0 auto',
        }}
      >
        <Routes>
          <Route path="/"            element={<HomePage />} />
          <Route path="/flashcards"  element={<FlashcardGame />} />
          <Route path="/synonyms"    element={<SynonymGame />} />
          <Route path="/fast-attack" element={<FastAttackGame />} />
          <Route path="/hard-terms"  element={<HardTermsGame />} />
          <Route path="/quant"       element={<QuantProblemTracker />} />
          <Route path="/geometry"    element={<GeometryLearning />} />
          <Route path="/time"        element={isPro ? <TimeStats /> : <Navigate to="/upgrade" replace />} />
          <Route path="/upgrade"     element={<UpgradePage />} />
          <Route path="/privacy"     element={<PrivacyPolicy />} />
          <Route path="/terms"       element={<TermsOfService />} />
          <Route path="*"            element={<Navigate to="/" replace />} />
        </Routes>
      </div>

      {/* ── Bottom tab bar ── */}
      <nav
        className="bottom-nav"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: 'calc(60px + env(safe-area-inset-bottom, 0px))',
          display: 'flex',
          alignItems: 'stretch',
          zIndex: 100,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {TAB_ROUTES.map(tab => {
          const active = isActive(tab.path);
          return (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                border: 'none',
                backgroundColor: 'transparent',
                color: active ? PRIMARY : TEXT_MUTED,
                cursor: 'pointer',
                padding: '6px 4px 2px',
                minHeight: 44,
              }}
            >
              <span style={{
                display: 'flex',
                lineHeight: 1,
                padding: 4,
                borderRadius: 8,
                backgroundColor: active ? '#D4EDE6' : 'transparent',
              }}>
                {TabIcons[tab.id]}
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: active ? 700 : 400,
                  letterSpacing: '0.01em',
                  lineHeight: 1,
                }}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </nav>

      <Analytics />
      <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
      <FeedbackModal open={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />
    </div>
  );
}

export default App;
